import React, { useEffect, useState, RefObject } from "react";

interface SimpleShifterArrowProps {
  fromId: string;
  toId: string;
  containerRef: RefObject<HTMLElement>;
  color?: string;
  shaftWidth?: number;
  tipSize?: number;
  borderWidth?: number;
}

const SimpleShifterArrowConnector: React.FC<SimpleShifterArrowProps> = ({
  fromId,
  toId,
  containerRef,
  color = "#D046E8",
  shaftWidth = 2,
  tipSize = 8,
  borderWidth = 4,
}) => {
  const [coords, setCoords] = useState({ x1: 0, y1: 0, x2: 0, y2: 0 });

  useEffect(() => {
    const update = () => {
      const container = containerRef.current;
      if (!container) return;
      const cr = container.getBoundingClientRect();
      const a = document.getElementById(fromId)?.getBoundingClientRect();
      const b = document.getElementById(toId)?.getBoundingClientRect();
      if (!a || !b) return;

      setCoords({
        x1: a.left + a.width  / 2 - cr.left,
        y1: a.top  + a.height / 2 - cr.top,
        x2: b.left + b.width  / 2 - cr.left,
        y2: b.top  + b.height / 2 - cr.top,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [fromId, toId, containerRef]);

  const { x1, y1, x2, y2 } = coords;
  if (x1 === 0 && y1 === 0 && x2 === 0 && y2 === 0) return null;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

  const circleSize = tipSize;
  const circleBorderSize = circleSize + borderWidth;

  return (
    <>
      {/* White border circle at start */}
      <div
        style={{
          position: "absolute",
          top: `${y1 - circleBorderSize / 2}px`,
          left: `${x1 - circleBorderSize / 2}px`,
          width: `${circleBorderSize}px`,
          height: `${circleBorderSize}px`,
          borderRadius: "50%",
          backgroundColor: "#fff",
          pointerEvents:   "none",
          zIndex: 999,
        }}
      />

      {/* Colored circle at start */}
      <div
        style={{
          position: "absolute",
          top: `${y1 - circleSize / 2}px`,
          left: `${x1 - circleSize / 2}px`,
          width: `${circleSize}px`,
          height: `${circleSize}px`,
          borderRadius: "50%",
          backgroundColor: color,
          pointerEvents: "none",
          zIndex: 1000,
        }}
      />

      {/* White border shaft */}
      <div
        style={{
          position: "absolute",
          top: `${y1 - (shaftWidth + borderWidth) / 2}px`,
          left: `${x1}px`,
          width: `${len}px`,
          height: `${shaftWidth + borderWidth}px`,
          backgroundColor: "#fff",
          transform: `rotate(${angle}deg)`,
          transformOrigin: "0 50%",
          pointerEvents: "none",
          zIndex: 997,
        }}
      />

      {/* Colored shaft */}
      <div
        style={{
          position: "absolute",
          top: `${y1 - shaftWidth / 2}px`,
          left: `${x1}px`,
          width: `${len}px`,
          height: `${shaftWidth}px`,
          backgroundColor: color,
          transform: `rotate(${angle}deg)`,
          transformOrigin: "0 50%",
          pointerEvents:  "none",
          zIndex: 998,
        }}
      />

      {/* White border tip */}
      <div
        style={{
          position: "absolute",
          top: `${y2 - (tipSize + borderWidth) / 2}px`,
          left: `${x2 - (tipSize + borderWidth) / 2}px`,
          width: 0,
          height: 0,
          borderTop: `${(tipSize + borderWidth) / 2}px solid transparent`,
          borderBottom: `${(tipSize + borderWidth) / 2}px solid transparent`,
          borderLeft: `${tipSize + borderWidth}px solid #fff`,
          transform: `rotate(${angle}deg)`,
          transformOrigin: "center center",
          pointerEvents: "none",
          zIndex: 997,
        }}
      />

      {/* Colored tip */}
      <div
        style={{
          position: "absolute",
          top: `${y2 - tipSize / 2}px`,
          left: `${x2 - tipSize / 2}px`,
          width: 0,
          height: 0,
          borderTop: `${tipSize / 2}px solid transparent`,
          borderBottom: `${tipSize / 2}px solid transparent`,
          borderLeft: `${tipSize}px solid ${color}`,
          transform: `rotate(${angle}deg)`,
          transformOrigin: "center center",
          pointerEvents: "none",
          zIndex: 998,
        }}
      />
    </>
  );
};

export default SimpleShifterArrowConnector;